import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useCustomRSVP } from '@/hooks/useCustomRSVP';
import { toast } from 'sonner';

interface CustomRSVPFormProps {
  eventId: string;
  guestId: string;
  guestEmail: string;
  onSubmitted: () => void;
}

export function CustomRSVPForm({ eventId, guestId, guestEmail, onSubmitted }: CustomRSVPFormProps) {
  const { rsvpForm, loading, error, submitResponse } = useCustomRSVP(eventId);
  const [answers, setAnswers] = useState<Record<string, string | string[]>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [visibleQuestions, setVisibleQuestions] = useState<Set<string>>(new Set());
  const [requiredQuestions, setRequiredQuestions] = useState<Set<string>>(new Set());

  // Initialize visible and required questions
  useEffect(() => {
    if (!rsvpForm?.questions) return;
    
    const initialVisible = new Set<string>();
    const initialRequired = new Set<string>();

    rsvpForm.questions.forEach(question => {
      initialVisible.add(question.id);
      if (question.required) {
        initialRequired.add(question.id);
      }
    });

    setVisibleQuestions(initialVisible);
    setRequiredQuestions(initialRequired);
  }, [rsvpForm]);

  // Apply conditional logic when answers change
  useEffect(() => {
    if (!rsvpForm?.questions) return;

    const newVisible = new Set<string>();
    const newRequired = new Set<string>();

    rsvpForm.questions.forEach(question => {
      let isVisible = true;
      let isRequired = question.required;

      // Check all conditional logic rules for this question
      rsvpForm.questions.forEach(triggerQuestion => {
        if (triggerQuestion.conditionalLogic) {
          triggerQuestion.conditionalLogic.forEach(rule => {
            if (rule.targetQuestionIds.includes(question.id)) {
              const triggerAnswer = answers[triggerQuestion.id];
              const shouldApplyRule = Array.isArray(triggerAnswer) 
                ? triggerAnswer.includes(rule.triggerValue)
                : triggerAnswer === rule.triggerValue;

              if (shouldApplyRule) {
                switch (rule.action) {
                  case 'show':
                    isVisible = true;
                    break;
                  case 'hide':
                    isVisible = false;
                    break;
                  case 'required':
                    isRequired = true;
                    break;
                  case 'skip':
                    isVisible = false;
                    break;
                }
              }
            }
          });
        }
      });

      if (isVisible) {
        newVisible.add(question.id);
      }
      if (isRequired && isVisible) {
        newRequired.add(question.id);
      }
    });

    setVisibleQuestions(newVisible);
    setRequiredQuestions(newRequired);
  }, [answers, rsvpForm]);

  const updateAnswer = (questionId: string, value: string | string[]) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!rsvpForm?.questions) return;

    // Validate required fields
    const missingRequired = rsvpForm.questions
      .filter(q => visibleQuestions.has(q.id) && requiredQuestions.has(q.id))
      .filter(q => !answers[q.id] || (Array.isArray(answers[q.id]) && answers[q.id].length === 0))
      .map(q => q.text);

    if (missingRequired.length > 0) {
      toast.error(`Please fill in required fields: ${missingRequired.join(', ')}`);
      return;
    }

    setIsSubmitting(true);
    try {
      // Filter answers to only include visible questions
      const filteredAnswers = Object.fromEntries(
        Object.entries(answers).filter(([questionId]) => visibleQuestions.has(questionId))
      );

      await submitResponse(guestId, filteredAnswers);
      onSubmitted();
    } catch (error) {
      console.error('Error submitting custom RSVP:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p>Loading RSVP form...</p>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center">
          <p className="text-destructive">Error loading RSVP form: {error}</p>
        </CardContent>
      </Card>
    );
  }

  if (!rsvpForm || !rsvpForm.questions || rsvpForm.questions.length === 0) {
    return null; // No custom form to show
  }

  const renderQuestion = (question: any) => {
    if (!visibleQuestions.has(question.id)) {
      return null;
    }

    const isRequired = requiredQuestions.has(question.id);
    const currentAnswer = answers[question.id];

    return (
      <div key={question.id} className="space-y-2">
        <Label className="flex items-center gap-2">
          {question.text}
          {isRequired && <span className="text-red-500">*</span>}
        </Label>
        
        {question.helpText && (
          <p className="text-sm text-muted-foreground">{question.helpText}</p>
        )}

        {question.type === 'text' && (
          <Input
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
          />
        )}

        {question.type === 'textarea' && (
          <Textarea
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
          />
        )}

        {question.type === 'email' && (
          <Input
            type="email"
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
          />
        )}

        {question.type === 'phone' && (
          <Input
            type="tel"
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
          />
        )}

        {question.type === 'number' && (
          <Input
            type="number"
            placeholder={question.placeholder}
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
          />
        )}

        {question.type === 'date' && (
          <Input
            type="date"
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
          />
        )}

        {question.type === 'time' && (
          <Input
            type="time"
            value={currentAnswer as string || ''}
            onChange={(e) => updateAnswer(question.id, e.target.value)}
          />
        )}

        {question.type === 'select' && question.options && (
          <Select
            value={currentAnswer as string || ''}
            onValueChange={(value) => updateAnswer(question.id, value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {question.options.map((option: string, index: number) => (
                <SelectItem key={index} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {question.type === 'radio' && question.options && (
          <RadioGroup
            value={currentAnswer as string || ''}
            onValueChange={(value) => updateAnswer(question.id, value)}
          >
            {question.options.map((option: string, index: number) => (
              <div key={index} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`${question.id}-${index}`} />
                <Label htmlFor={`${question.id}-${index}`}>{option}</Label>
              </div>
            ))}
          </RadioGroup>
        )}

        {(question.type === 'checkbox' || question.type === 'multiselect') && question.options && (
          <div className="space-y-2">
            {question.options.map((option: string, index: number) => (
              <div key={index} className="flex items-center space-x-2">
                <Checkbox
                  id={`${question.id}-${index}`}
                  checked={Array.isArray(currentAnswer) ? currentAnswer.includes(option) : false}
                  onCheckedChange={(checked) => {
                    const currentValues = Array.isArray(currentAnswer) ? currentAnswer : [];
                    if (checked) {
                      updateAnswer(question.id, [...currentValues, option]);
                    } else {
                      updateAnswer(question.id, currentValues.filter(v => v !== option));
                    }
                  }}
                />
                <Label htmlFor={`${question.id}-${index}`}>{option}</Label>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Additional Information</CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {rsvpForm.questions.map(renderQuestion)}
          
          <Button type="submit" className="w-full" disabled={isSubmitting}>
            {isSubmitting ? 'Submitting...' : 'Submit Additional Info'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}